import { Request } from "express";
import { inject, injectable } from "inversify";
import { DI } from "../../infrastructure/di";
import { ITracingSegment } from "../../infrastructure/tracing-segment.interface";
import { BobRequestSerializingComponent } from "../components/bobrequest/bobrequest-serializing-component";
import { BobrequestTranslatorComponent } from "../components/bobrequest/bobrequest-translator-component";
import { MicroserviceIdentifierComponent } from "../components/bobrequest/microservice-identifier-component";
import { MicroserviceLoaderComponent } from "../components/bobrequest/microservice-loader-component";
import { ITracker } from "../interfaces/tracker.interface";
import { BobRequest } from "../model/bob-request";
import { BobRequestPipeline } from "../pipelines/bob-request-pipeline";
import { IPipeline } from "../pipelines/pipeline.inferface";
import { PageRepository } from "../repositories/page-repository";
import { MicroservicePipelineFactory } from "./microservice-pipeline-factory";
import { TracingClassFactory } from "./tracing-class-factory";

@injectable()
export class BobrequestPipelineFactory {

	private readonly environment: string;
	private readonly tracker: ITracker;

	private tracingClassFactory = new TracingClassFactory();

	constructor(@inject(DI.ENVIRONMENT) environment: string, @inject(DI.TRACKER) tracker) {

		this.tracker = tracker;
		this.environment = environment;
		if (this.environment !== "tst" && this.environment !== "prd") {
			this.environment = "tst";
		}
	}

	public create(req: Request, proxyHost: string, proxyProtocol: string, proxyPort: string): IPipeline<Request, Promise<BobRequest>> {

		const segment: ITracingSegment = this.setUpTracing(req);

		const portalPageRepository = new PageRepository(proxyHost, proxyProtocol, proxyPort);

		const bobrequestTranslatorComponent = new BobrequestTranslatorComponent(portalPageRepository);
		const microserviceIdentifier = new MicroserviceIdentifierComponent(this.environment);
		const microserviceLoader = new MicroserviceLoaderComponent();
		const bobRequestSerializerComponent = new BobRequestSerializingComponent();

		const microserviceLoaderTracing = this.tracingClassFactory.createTracingComponent(segment, microserviceLoader);
		const microservicePipelineFactory = new MicroservicePipelineFactory(microserviceLoaderTracing, this.tracker);

		microserviceLoader.setMicroservicePipelineFactory(microservicePipelineFactory);

		return this.tracingClassFactory.createTracingPipeline(
			segment,
			new BobRequestPipeline(
				this.tracingClassFactory.createTracingComponent(segment, bobrequestTranslatorComponent),
				this.tracingClassFactory.createTracingComponent(segment, microserviceIdentifier),
				microserviceLoaderTracing,
				this.tracingClassFactory.createTracingComponent(segment, bobRequestSerializerComponent)
			)
		);
	}

	private setUpTracing(req: Request): ITracingSegment {
		return req["segment"].addNewSubsegment("BobrequestPipeline");
	}
}
